// components/Settings/EmailTemplates.jsx
import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Save,
  X,
  FileText,
  Search,
  Filter,
  ChevronDown,
  AlertCircle,
  Check,
  Code,
  Type,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const EmailTemplates = () => {
  const { isDarkMode } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [filterType]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== "all") {
        params.type = filterType;
      }
      const response = await apiService.getCompanyEmailTemplates(params);
      setTemplates(response.data.rows || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this template? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      await apiService.deleteCompanyEmailTemplate(templateId);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Failed to delete template");
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await apiService.duplicateCompanyEmailTemplate(template.id);
      fetchTemplates();
    } catch (error) {
      console.error("Error duplicating template:", error);
      alert("Failed to duplicate template");
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await apiService.updateCompanyEmailTemplate(template.id, {
        isActive: !template.isActive,
      });
      fetchTemplates();
    } catch (error) {
      console.error("Error toggling template status:", error);
      alert("Failed to update template status");
    }
  };

  const filteredTemplates = templates.filter((template) =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F]" : "bg-[#F4F5F7]"} p-6`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1
            className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"} mb-2`}
          >
            Email Templates
          </h1>
          <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Create and manage email templates for your team
          </p>
        </div>

        {/* Toolbar */}
        <div
          className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg p-4 mb-6`}
        >
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                size={20}
              />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:outline-none focus:border-blue-500`}
              />
            </div>

            {/* Filter and Actions */}
            <div className="flex gap-3 w-full lg:w-auto">
              <div className="relative flex-1 lg:flex-initial">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className={`w-full px-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:outline-none focus:border-blue-500 appearance-none pr-10`}
                >
                  <option value="all">All Types</option>
                  <option value="sales_engagement">Sales Engagement</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="nurture">Nurture</option>
                  <option value="welcome">Welcome</option>
                  <option value="thank_you">Thank You</option>
                  <option value="custom">Custom</option>
                </select>
                <ChevronDown
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-gray-400" : "text-gray-500"} pointer-events-none`}
                  size={16}
                />
              </div>

              <button
                onClick={handleCreateTemplate}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors whitespace-nowrap"
              >
                <Plus size={20} />
                Create Template
              </button>
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Loading templates...
            </div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div
            className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg p-12 text-center`}
          >
            <FileText
              className={`mx-auto mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
              size={48}
            />
            <h3
              className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"} mb-2`}
            >
              No templates found
            </h3>
            <p
              className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-6`}
            >
              {searchTerm
                ? "Try adjusting your search"
                : "Get started by creating your first email template"}
            </p>
            {!searchTerm && (
              <button
                onClick={handleCreateTemplate}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                Create Template
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={handleEditTemplate}
                onDelete={handleDeleteTemplate}
                onDuplicate={handleDuplicateTemplate}
                onToggleActive={handleToggleActive}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <TemplateModal
            template={editingTemplate}
            onClose={() => {
              setShowModal(false);
              setEditingTemplate(null);
            }}
            onSuccess={() => {
              setShowModal(false);
              setEditingTemplate(null);
              fetchTemplates();
            }}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </div>
  );
};

// Template Card Component
const TemplateCard = ({
  template,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleActive,
  isDarkMode,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const typeColors = {
    sales_engagement: "bg-blue-600",
    follow_up: "bg-green-600",
    nurture: "bg-purple-600",
    welcome: "bg-yellow-600",
    thank_you: "bg-pink-600",
    custom: "bg-gray-600",
  };

  const typeColor = typeColors[template.type] || "bg-gray-600";

  return (
    <div
      className={`${isDarkMode ? "bg-[#13161B] border-gray-700 hover:border-gray-600" : "bg-white border-gray-200 hover:border-gray-300"} border rounded-lg p-6 transition-all`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0 mr-3">
          <h3
            className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"} mb-1 truncate`}
          >
            {template.name}
          </h3>
          <span
            className={`inline-block px-2 py-1 rounded text-xs font-semibold text-white ${typeColor}`}
          >
            {template.type.replace("_", " ").toUpperCase()}
          </span>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-2">
          {template.isDefault && (
            <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 rounded text-xs font-semibold">
              Default
            </span>
          )}
          <button
            onClick={() => onToggleActive(template)}
            className={`p-1 rounded transition-colors ${
              template.isActive
                ? "text-green-400 hover:bg-green-400/10"
                : isDarkMode
                  ? "text-gray-500 hover:bg-gray-700"
                  : "text-gray-400 hover:bg-gray-200"
            }`}
            title={template.isActive ? "Active" : "Inactive"}
          >
            {template.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
        </div>
      </div>

      {/* Description */}
      {template.description && (
        <p
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4 line-clamp-2`}
        >
          {template.description}
        </p>
      )}

      {/* Subject Preview */}
      <div
        className={`${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-100"} rounded-lg p-3 mb-4`}
      >
        <div
          className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"} mb-1`}
        >
          Subject
        </div>
        <div
          className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"} truncate`}
        >
          {template.subject}
        </div>
      </div>

      {/* Stats */}
      <div
        className={`flex items-center gap-4 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4`}
      >
        <span>Used {template.usageCount || 0} times</span>
        {template.lastUsedAt && (
          <span>
            Last used{" "}
            {new Date(template.lastUsedAt).toLocaleDateString("en-GB")}
          </span>
        )}
      </div>

      {/* Variables */}
      {template.variables && template.variables.length > 0 && (
        <div className="mb-4">
          <div
            className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"} mb-2`}
          >
            Variables
          </div>
          <div className="flex flex-wrap gap-1">
            {template.variables.slice(0, 3).map((variable, index) => (
              <span
                key={index}
                className={`px-2 py-1 ${isDarkMode ? "bg-gray-700/50 text-gray-300" : "bg-gray-200 text-gray-600"} rounded text-xs font-mono`}
              >
                {`{{${variable}}}`}
              </span>
            ))}
            {template.variables.length > 3 && (
              <span
                className={`px-2 py-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"} text-xs`}
              >
                +{template.variables.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div
        className={`flex gap-2 pt-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        <button
          onClick={() => onEdit(template)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
        >
          <Edit size={16} />
          Edit
        </button>
        <button
          onClick={() => onDuplicate(template)}
          className={`px-3 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 hover:bg-gray-800 text-gray-300" : "bg-gray-100 border-gray-200 hover:bg-gray-200 text-gray-600"} border rounded-lg transition-colors`}
          title="Duplicate"
        >
          <Copy size={16} />
        </button>
        <button
          onClick={() => onDelete(template.id)}
          className={`px-3 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 hover:bg-red-900/20 hover:border-red-700 text-gray-300 hover:text-red-400" : "bg-gray-100 border-gray-200 hover:bg-red-100 hover:border-red-300 text-gray-600 hover:text-red-600"} border rounded-lg transition-colors`}
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

// Template Modal Component
const TemplateModal = ({ template, onClose, onSuccess, isDarkMode }) => {
  const [formData, setFormData] = useState({
    name: template?.name || "",
    description: template?.description || "",
    subject: template?.subject || "",
    body: template?.body || "",
    bodyHtml: template?.bodyHtml || "",
    type: template?.type || "custom",
    category: template?.category || "",
    isActive: template?.isActive ?? true,
    isDefault: template?.isDefault || false,
  });

  const [viewMode, setViewMode] = useState("text"); // 'text' or 'html'
  const [submitting, setSubmitting] = useState(false);
  const [showVariableHelper, setShowVariableHelper] = useState(false);

  const availableVariables = {
    contact: [
      "fullName",
      "firstName",
      "lastName",
      "email",
      "phone",
      "companyName",
      "title",
    ],
    appointment: [
      "appointmentTitle",
      "appointmentDate",
      "appointmentTime",
      "appointmentLocation",
      "appointmentDescription",
      "assignedTo",
      "telesalesPerson",
      "senderName",
    ],
    company: ["companyWebsite"],
    opportunity: ["opportunityName", "opportunityAmount", "opportunityStage"],
    other: ["currentDate", "currentTime", "teamName"],
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById(
      viewMode === "html" ? "bodyHtml" : "body",
    );
    const cursorPos = textarea.selectionStart;
    const textBefore = formData[
      viewMode === "html" ? "bodyHtml" : "body"
    ].substring(0, cursorPos);
    const textAfter =
      formData[viewMode === "html" ? "bodyHtml" : "body"].substring(cursorPos);
    const newText = `${textBefore}{{${variable}}}${textAfter}`;

    setFormData({
      ...formData,
      [viewMode === "html" ? "bodyHtml" : "body"]: newText,
    });

    // Focus back on textarea
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        cursorPos + variable.length + 4,
        cursorPos + variable.length + 4,
      );
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Extract variables from body and bodyHtml
      const bodyText = formData.body + " " + formData.bodyHtml;
      const variableMatches = bodyText.match(/\{\{(\w+)\}\}/g) || [];
      const extractedVariables = [
        ...new Set(variableMatches.map((match) => match.replace(/[{}]/g, ""))),
      ];

      const templateData = {
        ...formData,
        variables: extractedVariables,
      };

      if (template) {
        await apiService.updateCompanyEmailTemplate(template.id, templateData);
      } else {
        await apiService.createCompanyEmailTemplate(templateData);
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving template:", error);
      alert(error.response?.data?.message || "Failed to save template");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-xl border max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div
          className={`p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex items-center justify-between`}
        >
          <h2
            className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {template ? "Edit Template" : "Create Template"}
          </h2>
          <button
            onClick={onClose}
            className={`${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"} transition-colors`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
                >
                  Template Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:border-blue-500 focus:outline-none`}
                  placeholder="e.g., Appointment Confirmation"
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
                >
                  Type <span className="text-red-400">*</span>
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:border-blue-500 focus:outline-none`}
                >
                  <option value="sales_engagement">Sales Engagement</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="nurture">Nurture</option>
                  <option value="welcome">Welcome</option>
                  <option value="thank_you">Thank You</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
              >
                Category (optional)
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={`w-full px-4 py-3 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:border-blue-500 focus:outline-none`}
                placeholder="e.g., Appointments, Proposals, etc."
              />
            </div>

            <div>
              <label
                className={`block text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
              >
                Description (optional)
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
                className={`w-full px-4 py-3 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:border-blue-500 focus:outline-none`}
                placeholder="Brief description of when to use this template"
              />
            </div>

            {/* Subject */}
            <div>
              <label
                className={`block text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
              >
                Email Subject <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:border-blue-500 focus:outline-none`}
                placeholder="e.g., Appointment Confirmed - {{appointmentDate}}"
              />
            </div>

            {/* Variable Helper */}
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
              <button
                type="button"
                onClick={() => setShowVariableHelper(!showVariableHelper)}
                className="flex items-center gap-2 text-blue-400 hover:text-blue-300 font-semibold mb-3"
              >
                <AlertCircle size={18} />
                Available Variables (click to expand)
                <ChevronDown
                  size={16}
                  className={`transform transition-transform ${showVariableHelper ? "rotate-180" : ""}`}
                />
              </button>

              {showVariableHelper && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(availableVariables).map(
                    ([category, vars]) => (
                      <div key={category}>
                        <div
                          className={`text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-2 uppercase`}
                        >
                          {category}
                        </div>
                        <div className="space-y-1">
                          {vars.map((variable) => (
                            <button
                              key={variable}
                              type="button"
                              onClick={() => insertVariable(variable)}
                              className={`block w-full text-left px-2 py-1 text-xs font-mono text-blue-300 ${isDarkMode ? "hover:bg-blue-900/30" : "hover:bg-blue-100"} rounded transition-colors`}
                            >
                              {`{{${variable}}}`}
                            </button>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>

            {/* Body Editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label
                  className={`block text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Email Body <span className="text-red-400">*</span>
                </label>
                <div
                  className={`flex gap-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-100 border-gray-200"} border rounded-lg p-1`}
                >
                  <button
                    type="button"
                    onClick={() => setViewMode("text")}
                    className={`px-3 py-1 rounded text-sm font-semibold transition-colors flex items-center gap-1 ${
                      viewMode === "text"
                        ? "bg-blue-600 text-white"
                        : isDarkMode
                          ? "text-gray-400 hover:text-white"
                          : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    <Type size={14} />
                    Plain Text
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("html")}
                    className={`px-3 py-1 rounded text-sm font-semibold transition-colors flex items-center gap-1 ${
                      viewMode === "html"
                        ? "bg-blue-600 text-white"
                        : isDarkMode
                          ? "text-gray-400 hover:text-white"
                          : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    <Code size={14} />
                    HTML
                  </button>
                </div>
              </div>

              {viewMode === "text" ? (
                <textarea
                  id="body"
                  name="body"
                  value={formData.body}
                  onChange={handleChange}
                  required
                  rows={12}
                  className={`w-full px-4 py-3 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:border-blue-500 focus:outline-none font-mono text-sm`}
                  placeholder="Enter your email body here. Use {{variableName}} for dynamic content."
                />
              ) : (
                <textarea
                  id="bodyHtml"
                  name="bodyHtml"
                  value={formData.bodyHtml}
                  onChange={handleChange}
                  rows={12}
                  className={`w-full px-4 py-3 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:border-blue-500 focus:outline-none font-mono text-sm`}
                  placeholder="Enter HTML email body here. Use {{variableName}} for dynamic content."
                />
              )}
              <p
                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"} mt-1`}
              >
                Click variable names above to insert them at cursor position
              </p>
            </div>

            {/* Options */}
            <div className="flex gap-4">
              <label
                className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"} cursor-pointer`}
              >
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                />
                Active (available for use)
              </label>
              <label
                className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"} cursor-pointer`}
              >
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleChange}
                  className="rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                />
                Set as default for this type
              </label>
            </div>
          </div>

          {/* Footer */}
          <div
            className={`p-6 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex justify-end gap-3`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`px-6 py-3 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-800" : "bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200"} border rounded-lg transition-colors font-medium`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  {template ? "Update Template" : "Create Template"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailTemplates;
